import { filter, get, padStart } from 'lodash';

import {
  getHiddenVariablesForNode,
  getLogicPathsForNode,
} from 'containers/QuestionnaireLogic/utils';
import { QUESTIONS_COLLAPSIBLE } from 'data/featuresPerQuestion';
import {
  isAbGroup,
  isGroup,
  isBasicGroup,
  isConjointGroup,
  isVanWestendorpGroup,
  isMaxDiffGroup,
  isConjointOrMaxDiffGroup,
  isLoopAndMergeGroup,
  isMatrixSingleMultipleQuestionType,
} from 'utils/nodeTypes';

import { ANSWER_NONE_OF_THE_ABOVE, ANSWER_OTHER } from './constants';
import { getQuestionPlaceholderHeight } from './GenericQuestion/utils';
import { getScreeningBoundary } from './screeningBoundaryUtils';

function canDuplicate(nodeType, isScreening, canAddScreeningQuestions) {
  if (isScreening) {
    return canAddScreeningQuestions;
  }

  return !isAbGroup(nodeType) && !isConjointOrMaxDiffGroup(nodeType);
}

function pendingComments(comments, questionRefId) {
  return filter(comments, { questionRefId, pending: true });
}

function resolveDisplayLabel(alias, fallback) {
  if (alias != null && String(alias).trim() !== '') {
    return alias;
  }

  return fallback;
}

function mapWithDisplayLabels(items = [], getFallbackLabel) {
  if (!items?.length) {
    return items;
  }
  let nonAliasIndex = items.filter((item) => item.alias).length;

  return items.map((item) => {
    nonAliasIndex = item.alias ? nonAliasIndex : nonAliasIndex + 1;
    return {
      ...item,
      label: resolveDisplayLabel(item.alias, getFallbackLabel(nonAliasIndex)),
    };
  });
}

function isSpecialAnswerType(type) {
  return type === ANSWER_OTHER || type === ANSWER_NONE_OF_THE_ABOVE;
}

export function getAnswerDisplayLabel(answer, fallback = '') {
  if (isSpecialAnswerType(answer?.type)) {
    return resolveDisplayLabel(answer.alias, fallback);
  }

  return answer?.label;
}

export function getAnswerDragDisplayLabel(answer, index) {
  const fallback = padStart(index + 1, 2, '0');
  return getAnswerDisplayLabel(answer, fallback) || fallback;
}

export function getMatrixOptionDragDisplayLabel(
  item,
  index,
  { isAttribute = false } = {},
) {
  const fallback = isAttribute ? `A${index + 1}` : padStart(index + 1, 2, '0');

  if (item?.alias != null && String(item.alias).trim() !== '') {
    return item.alias;
  }

  return item?.label || fallback;
}

function mapAnswersWithDisplayLabels(items = []) {
  if (!items?.length) {
    return items;
  }

  let nonAliasIndex = items.filter(
    (item) => !isSpecialAnswerType(item.type) && item.alias,
  ).length;

  return items.map((item) => {
    if (item.type === ANSWER_NONE_OF_THE_ABOVE) {
      return {
        ...item,
        alias: resolveDisplayLabel(item.alias, 'NON'),
      };
    }

    if (item.type === ANSWER_OTHER) {
      return {
        ...item,
        alias: resolveDisplayLabel(item.alias, 'OTH'),
      };
    }

    nonAliasIndex = item.alias ? nonAliasIndex : nonAliasIndex + 1;

    return {
      ...item,
      label: resolveDisplayLabel(item.alias, padStart(nonAliasIndex, 2, '0')),
    };
  });
}

export function formatChildEntityLabels(entity) {
  const updated = { ...entity };

  if (updated.answers) {
    updated.answers = mapAnswersWithDisplayLabels(updated.answers);
  }

  if (updated.scorepoints) {
    updated.scorepoints = mapAnswersWithDisplayLabels(updated.scorepoints);
  }

  if (updated.attributes) {
    updated.attributes = mapWithDisplayLabels(
      updated.attributes,
      (index) => `A${index}`,
    );
  }

  if (updated.matrix) {
    const matrix = { ...updated.matrix };

    if (matrix.columns) {
      matrix.columns = mapWithDisplayLabels(matrix.columns, (index) =>
        padStart(index, 2, '0'),
      );
    }

    if (matrix.matrixAnswers) {
      matrix.matrixAnswers = mapWithDisplayLabels(
        matrix.matrixAnswers,
        (index) => padStart(index, 2, '0'),
      );
    }

    updated.matrix = matrix;
  }

  return updated;
}

function getChildItemsForStateKey(question, stateKey, nodeType) {
  if (stateKey === 'answers' || stateKey === 'rows') {
    return question.answers;
  }

  if (stateKey === 'scorepoints') {
    return question.scorepoints;
  }

  if (stateKey === 'columns') {
    if (isMatrixSingleMultipleQuestionType(nodeType)) {
      return question.scorepoints;
    }

    return question.matrix?.columns;
  }

  if (stateKey === 'matrixAnswers') {
    return question.matrix?.matrixAnswers;
  }

  if (stateKey === 'matrixExtraLabels') {
    return question.matrix?.matrixExtraLabels;
  }

  return question.answers;
}

export function formatQuestionChildLabelsAfterMove(
  question,
  { stateKey, indexA, indexB, nodeType },
) {
  const items = getChildItemsForStateKey(question, stateKey, nodeType) || [];
  const a = items[indexA];
  const b = items[indexB];

  if (hasDisplayAlias(a) || hasDisplayAlias(b)) {
    return question;
  }

  return formatChildEntityLabels(question);
}

export function questionDraftFromComponentState(question, state, nodeType) {
  if (isMatrixSingleMultipleQuestionType(nodeType)) {
    return {
      ...question,
      answers: state.rows ?? question.answers,
      scorepoints: state.columns ?? question.scorepoints,
    };
  }

  return {
    ...question,
    answers: state.answers ?? question.answers,
    matrix: {
      ...question.matrix,
      matrixAnswers: state.matrixAnswers ?? question.matrix?.matrixAnswers,
      columns: state.columns ?? question.matrix?.columns,
      matrixExtraLabels:
        state.matrixExtraLabels ?? question.matrix?.matrixExtraLabels,
    },
  };
}

export function applyFormattedLabelsToLikertState(formatted, state) {
  return {
    ...state,
    rows: formatted.answers ?? state.rows,
    columns: formatted.scorepoints ?? state.columns,
  };
}

export function applyFormattedLabelsToMatrixOpenEndedState(formatted, state) {
  return {
    ...state,
    answers: formatted.answers ?? state.answers,
    matrixAnswers: formatted.matrix?.matrixAnswers ?? state.matrixAnswers,
    columns: formatted.matrix?.columns ?? state.columns,
    matrixExtraLabels:
      formatted.matrix?.matrixExtraLabels ?? state.matrixExtraLabels,
  };
}

export function hasDisplayAlias(item) {
  return item?.alias != null && String(item.alias).trim() !== '';
}

export function getDisplayLabelWithGroupPrefix(node, nodes = []) {
  if (!node?.groupRefId) {
    return node?.label;
  }

  const parentGroup = nodes.find(({ refId }) => refId === node.groupRefId);

  if (!parentGroup?.label) {
    return node.label;
  }

  return `${parentGroup.label} ${node.label}`;
}

// Same as getDisplayLabelWithGroupPrefix, but resolves the parent group from a
// refId-keyed map (as used by the recall/mention feature) instead of an array.
export function getGroupPrefixedLabelFromMap(node, nodesByRefId = {}) {
  if (!node?.groupRefId) {
    return node?.label;
  }

  const parentGroup = nodesByRefId[node.groupRefId];

  if (!parentGroup?.label) {
    return node.label;
  }

  return `${parentGroup.label} ${node.label}`;
}

export function findFormattedNodeByRefId(nodes = [], refId) {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];

    if (node.refId === refId) {
      return node;
    }

    if (node.questions?.length) {
      const subQuestion = node.questions.find(
        (question) => question.refId === refId,
      );

      if (subQuestion) {
        return subQuestion;
      }
    }
  }

  return null;
}

export function countAliasedQuestions(questions) {
  let aliasedQuestionsCount = 0;
  let aliasedScreeningQuestionsCount = 0;
  questions.forEach((question) => {
    if (isGroup(question.type)) {
      aliasedQuestionsCount += question.questions.filter((q) => q.alias).length;
    } else if (!question.isScreening && question.alias) {
      aliasedQuestionsCount += 1;
    } else if (question.isScreening && question.alias) {
      aliasedScreeningQuestionsCount += 1;
    }
  });
  return [aliasedQuestionsCount, aliasedScreeningQuestionsCount];
}

export function getFormattedNodes(
  questions = [],
  logicPaths = [],
  logicErrors = {},
  activeQuestionId,
  surveyComments = [],
  variableActionsPerNode = {},
  hiddenVariables = [],
  canAddScreeningQuestions = false,
  hiddenNodes = [],
  enforceScreeningBoundary = false,
) {
  const {
    unusedNodes = [],
    circleErrors = [],
    invalidReferences = [],
    invalidReferencesAnswersLogic = [],
    invalidReferencesVariablesLogic = [],
  } = logicErrors;

  // The first non alias question index starts after
  // we counted all the aliased questions
  let [nonAliasQuestionIndex, nonAliasScreeningQuestionIndex] =
    countAliasedQuestions(questions);
  // We only want to show logic paths when there are created rules. PathsWithLogic only contain pairs that come
  // from logic paths
  let questionIndex = 0;
  let groupIndex = 0;
  let vanWestendorpGroupIndex = 0;
  let label = '';
  const allQuestionsLength = questions.length;
  const screeningQuestionsLength = questions.filter(
    (q) => q.isScreening,
  ).length;
  const extendedQuestions = questions.map((question, index) => {
    const comments = pendingComments(surveyComments.comments, question.refId);

    const hasPrevious = index > 0;
    const hasNext = index < allQuestionsLength - 1;
    let canMoveUp = hasPrevious;
    let canMoveDown = hasNext;

    if (enforceScreeningBoundary) {
      const { lastScreeningIndex, firstNonScreeningIndex } =
        getScreeningBoundary(questions);

      if (question.isScreening && index === lastScreeningIndex) {
        canMoveDown = false;
      }
      if (!question.isScreening && index === firstNonScreeningIndex) {
        canMoveUp = false;
      }
    }

    const previousQuestionRefId = get(questions, `[${index - 1}].refId`);
    const nextQuestionRefId = get(questions, `[${index + 1}].refId`);

    let isSubQuestionActive = false;
    let subQuestions = question.questions;

    if (isGroup(question.type)) {
      let defaultGroupLabel = label;

      if (isBasicGroup(question.type) || isLoopAndMergeGroup(question.type)) {
        groupIndex += 1;
        defaultGroupLabel = `Group ${groupIndex}`;
      }

      if (isAbGroup(question.type)) {
        defaultGroupLabel = 'A/B Test';
      }

      if (isConjointGroup(question.type)) {
        defaultGroupLabel = 'Conjoint';
      }

      const groupIsVanWestendorp = isVanWestendorpGroup(question.type);
      const groupIsDisplaySamePage = question.displaySamePage;

      if (groupIsVanWestendorp) {
        vanWestendorpGroupIndex += 1;
        defaultGroupLabel = `Pricing study (Van Westendorp) ${vanWestendorpGroupIndex}`;
      }

      if (isMaxDiffGroup(question.type)) {
        defaultGroupLabel = 'Maxdiff';
      }

      label = resolveDisplayLabel(question.alias, defaultGroupLabel);

      subQuestions = subQuestions.map((subQuestion, subQuestionIndex) => {
        if (!isSubQuestionActive) {
          isSubQuestionActive = activeQuestionId === subQuestion.refId;
        }

        const subQuestionCanMoveUp = groupIsVanWestendorp
          ? subQuestionIndex > 1
          : subQuestionIndex > 0;
        const subQuestionCanMoveDown = groupIsVanWestendorp
          ? subQuestionIndex < subQuestions.length - 1 && subQuestionIndex !== 0
          : subQuestionIndex < subQuestions.length - 1;

        questionIndex += 1;
        nonAliasQuestionIndex = subQuestion.alias
          ? nonAliasQuestionIndex
          : nonAliasQuestionIndex + 1;

        return formatChildEntityLabels({
          ...subQuestion,
          index: subQuestionIndex,
          questionIndex: questionIndex - screeningQuestionsLength,
          label: resolveDisplayLabel(
            subQuestion.alias,
            `Q${nonAliasQuestionIndex}`,
          ),
          isActive: activeQuestionId === subQuestion.refId,
          isLastElement: subQuestionIndex === subQuestions.length - 1,
          isCollapsible: QUESTIONS_COLLAPSIBLE[subQuestion.type],
          isFavorite: subQuestion.isFavorite,
          placeholderHeight: getQuestionPlaceholderHeight(subQuestion),
          hasPrevious: subQuestionIndex > 0,
          hasNext: subQuestionIndex < subQuestions.length - 1,
          canMoveUp: subQuestionCanMoveUp,
          canMoveDown: subQuestionCanMoveDown,
          canDuplicate: !groupIsVanWestendorp,
          canDelete: !groupIsVanWestendorp,
          canMoveQuestion:
            !groupIsVanWestendorp &&
            (!!subQuestion.groupRefId || hasNext || hasPrevious),
          canUpdateQuestionType: !groupIsVanWestendorp,
          canChangeToGroupType: false,
          inVanWestendorpGroup: groupIsVanWestendorp,
          inDisplaySamePageGroup: groupIsDisplaySamePage,
          previousQuestionRefId: get(
            subQuestions,
            `[${subQuestionIndex - 1}].refId`,
          ),
          nextQuestionRefId: get(
            subQuestions,
            `[${subQuestionIndex + 1}].refId`,
          ),
          comments: pendingComments(surveyComments.comments, subQuestion.refId),
          isUnused: unusedNodes.includes(subQuestion.refId),
          invalidReferences: invalidReferences[subQuestion.refId],
          invalidReferencesAnswersLogic:
            invalidReferencesAnswersLogic[subQuestion.refId],
          invalidReferencesVariablesLogic:
            invalidReferencesVariablesLogic[subQuestion.refId],
          isHidden: hiddenNodes.includes(subQuestion.refId),
          isParentGroupNodeHidden: hiddenNodes.includes(question.refId),
        });
      });
    } else if (question.isScreening) {
      questionIndex += 1;
      nonAliasScreeningQuestionIndex = question.alias
        ? nonAliasScreeningQuestionIndex
        : nonAliasScreeningQuestionIndex + 1;
      label = resolveDisplayLabel(
        question.alias,
        `SQ${nonAliasScreeningQuestionIndex}`,
      );
    } else {
      questionIndex += 1;
      nonAliasQuestionIndex = question.alias
        ? nonAliasQuestionIndex
        : nonAliasQuestionIndex + 1;
      label = resolveDisplayLabel(question.alias, `Q${nonAliasQuestionIndex}`);
    }
    let indexToUse = question.isScreening
      ? questionIndex
      : questionIndex - screeningQuestionsLength;

    if (isGroup(question.type)) {
      if (isVanWestendorpGroup(question.type)) {
        indexToUse = vanWestendorpGroupIndex;
      } else {
        indexToUse = groupIndex;
      }
    }

    const formattedNode = formatChildEntityLabels({
      ...question,
      index,
      questionIndex: indexToUse,
      label,
      hasPrevious,
      hasNext,
      canMoveUp,
      canMoveDown,
      canDuplicate: canDuplicate(
        question.type,
        question.isScreening,
        canAddScreeningQuestions,
      ),
      canDelete: true,
      canMoveQuestion: !!question.groupRefId || hasNext || hasPrevious,
      canUpdateQuestionType: true,
      comments,
      previousQuestionRefId,
      nextQuestionRefId,
      placeholderHeight: getQuestionPlaceholderHeight(question, subQuestions),
      isCollapsible: QUESTIONS_COLLAPSIBLE[question.type],
      isLastElement: index === questions.length - 1,
      isActive: activeQuestionId === question.refId,
      invalidReferences: invalidReferences[question.refId],
      invalidReferencesAnswersLogic:
        invalidReferencesAnswersLogic[question.refId],
      invalidReferencesVariablesLogic:
        invalidReferencesVariablesLogic[question.refId],
      isUnused: unusedNodes.includes(question.refId),
      isFavorite: question.isFavorite,
      questions: subQuestions,
      isSubQuestionActive,
      isHidden: hiddenNodes.includes(question.refId),
    });

    return formattedNode;
  });

  return extendedQuestions.map((node) => {
    const newNode = mapperForExtendedNodes({
      node,
      logicPaths,
      circleErrors,
      variableActionsPerNode,
      hiddenVariables,
      nodes: extendedQuestions,
    });

    if (node.questions) {
      newNode.questions = newNode.questions.map((subNode) =>
        mapperForExtendedNodes({
          node: subNode,
          logicPaths,
          circleErrors,
          variableActionsPerNode,
          hiddenVariables,
          nodes: extendedQuestions,
        }),
      );
    }

    return newNode;
  });
}

function mapperForExtendedNodes({
  node,
  nodes,
  logicPaths,
  circleErrors,
  variableActionsPerNode,
  hiddenVariables,
}) {
  return {
    ...node,
    ...getLogicPathsForNode({
      nodeRefId: node.refId,
      logicPaths,
      questions: nodes,
      errors: circleErrors,
    }),
    ...getHiddenVariablesForNode({
      nodeRefId: node.refId,
      variableActionsPerNode,
      hiddenVariables,
    }),
  };
}
